import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Gift } from "lucide-react";

/**
 * FreeToolsMenu — shared "Free Tools" nav dropdown used across all major
 * page headers (desktop dropdown + mobile list group). Fires a GA4
 * `free_tools_nav_click` event per item so we learn which tool people hunt for.
 */
export const FREE_TOOLS = [
  { to: "/workbook", label: "Free I GET TO Workbook", ga: "workbook" },
  { to: "/tap-in-calendar", label: "12-Month I GET TO Calendar", ga: "calendar" },
  { to: "/code-builder", label: "Code Builder", ga: "code_builder" },
];

const fireGA = (tool, where) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "free_tools_nav_click", { tool, location: where });
  }
};

// Desktop dropdown — click to toggle; closes on outside-click or item select.
export const FreeToolsDropdown = ({ color = "#068fa2", hoverColor = "#25a1c2", testidPrefix = "" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 font-sans font-medium transition-colors"
        style={{ color }}
        onMouseEnter={(e) => (e.currentTarget.style.color = hoverColor)}
        onMouseLeave={(e) => (e.currentTarget.style.color = color)}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid={`${testidPrefix}free-tools-toggle`}
      >
        Free Tools <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 mt-2 w-64 rounded-xl bg-white shadow-xl border border-[#CCFBF1] py-2 z-50"
          data-testid={`${testidPrefix}free-tools-menu`}
        >
          {FREE_TOOLS.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              onClick={() => { fireGA(t.ga, "desktop"); setOpen(false); }}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#042F2E] hover:bg-[#F0F5F4] transition-colors"
              data-testid={`${testidPrefix}free-tools-item-${t.ga}`}
            >
              <Gift className="w-4 h-4 text-[#25a1c2]" /> {t.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// Mobile list group — rendered inside an already-open hamburger drawer.
export const FreeToolsMobileLinks = ({ onNavigate, testidPrefix = "" }) => (
  <div className="border-b border-[#CCFBF1]/40" data-testid={`${testidPrefix}free-tools-mobile-group`}>
    <p className="px-3 pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-[#25a1c2]">Free Tools</p>
    {FREE_TOOLS.map((t) => (
      <Link
        key={t.to}
        to={t.to}
        onClick={() => { fireGA(t.ga, "mobile"); if (onNavigate) onNavigate(); }}
        className="flex items-center gap-2 py-2.5 px-3 rounded-md text-[#042F2E] hover:bg-[#CCFBF1] text-base"
        data-testid={`${testidPrefix}free-tools-mobile-item-${t.ga}`}
      >
        <Gift className="w-4 h-4 text-[#25a1c2]" /> {t.label}
      </Link>
    ))}
  </div>
);
